import { DocumentService } from './../../services/document.service';
import { Router } from '@angular/router';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  BasicDocument,
  FlDocument
} from '../../../core/declarations/document.interface';
import { ModalService } from '@bling-fe/shared/modals/base-modal';
import { ConfirmModalComponent } from '@bling-fe/shared/modals/confirm-modal';
import { marker as _ } from '@bling-fe/shared/utils';
import { DocumentImageComponent } from '../document-image/document-image.component';
import { AlertController, IonicModule } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePipe, NgIf } from '@angular/common';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { RelativeTimePipe } from '../../pipes/relative-time.pipe';
import { PdfModalComponent } from '../../../modals/pdf-modal/pdf-modal.component';
import { VideoModalComponent } from '../../../modals/video-modal/video-modal.component';
import { AudioModalComponent } from '../../../modals/audio-modal/audio-modal.component';
import { GallerySlideshowModalComponent } from '../../../modals/gallery-slideshow/gallery-slideshow-modal.component';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { DocumentRepository } from '../../repositories/document.repository';
import { ArticleRepository } from '../../repositories/article.repository';
import { catchError, map, Observable, of, take } from 'rxjs';
import { AppLinkService } from '../../services/app-link.service';
import { ShareKind } from '../../../modals/share-modal/share-modal.component';
import { NotificationService } from '../../services/notification.service';
import {
  MoveToFolderModalComponent,
  MoveToFolderModalProps,
  MoveToFolderModalResult
} from '../../../modals/move-to-folder-modal/move-to-folder-modal.component';

_('COMMON.LABELS.PREVIEW');
_('COMMON.LABELS.DOWNLOAD');
_('COMPONENTS.MEDIA.PLAY_VIDEO');
_('COMPONENTS.MEDIA.PLAY_AUDIO');

// HTMLMediaElement.error codes. Written out rather than read off MediaError,
// which not every runtime the component runs in provides.
const MEDIA_ERR_ABORTED = 1;
const MEDIA_ERR_DECODE = 3;

// The player, the renewal and the two dead ends are one state rather than a
// flag each, so no path can leave the spinner behind.
type MediaState = 'playing' | 'renewing' | 'unplayable' | 'unavailable';

// Re-reading the record either hands out a fresh signature, says the reader
// may not see this document, or fails for reasons that say nothing about the
// file at all - a renewal that failed must not be read as a broken file.
type UrlRenewal =
  | { kind: 'renewed'; url: string }
  | { kind: 'gone' }
  | { kind: 'failed' };

@Component({
  selector: 'fl-document-item',
  templateUrl: './document-item.component.html',
  styleUrls: ['./document-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DocumentImageComponent,
    IonicModule,
    TranslateModule,
    NgIf,
    SvgIconComponent,
    DatePipe,
    RelativeTimePipe
  ]
})
export class DocumentItemComponent implements OnChanges {
  @Input() item: BasicDocument;
  // Article attachments are not Document records; they refresh via the article endpoint.
  @Input() articleId: number | null = null;
  @Input() withPadding = false;
  @Input() showOptions = false;
  // The participation view lists documents from every folder, so a row has to say
  // where it came from. Not a link - the whole title area is already an anchor.
  @Input() folderName?: string;
  @Input() isAdmin = false;
  // A chat message already carries its time, so its attachments do not repeat it.
  @Input() showUpdatedAt = true;
  // Inside a chat bubble every attachment is a bordered card, so a document
  // and a video read as the same kind of thing. Media is always a card.
  @Input() card = false;
  @Input() showLinkAction = false;
  // A chat plays its media in place. A document list keeps one line per
  // file: a video or a recording is a tile with a play mark, played in a
  // modal.
  @Input() inlineMedia = true;
  // Folder lists only: a chat attachment has no folder to leave.
  @Input() showMoveAction = false;
  /** The document left this list: moved or deleted. */
  @Output() changed = new EventEmitter<void>();

  // Marks the row as pointing at a file filed elsewhere. On the host so it does
  // not fight the existing [class] binding on ion-item.
  @HostBinding('class.is-shortcut') get isShortcut(): boolean {
    return !!this.folderName;
  }

  // A second tap during the fresh-url fetch would stack a second modal.
  private opening = false;

  private mediaState: MediaState = 'playing';

  /** The player has its metadata and paints a frame instead of a black box. */
  mediaReady = false;

  /** One renewal per document, so a file nobody can decode cannot loop. */
  private urlRenewed = false;

  /** The thumbnail could not load its frame; the type icon stands in. */
  thumbFailed = false;

  get mediaPlayable(): boolean {
    return this.mediaState === 'playing';
  }

  /** The player refused the source and a freshly signed url is on its way. */
  get mediaReloading(): boolean {
    return this.mediaState === 'renewing';
  }

  /** Even the fresh url failed: this browser has no decoder for the file. */
  get mediaUnplayable(): boolean {
    return this.mediaState === 'unplayable';
  }

  /** The record is gone, or the reader may not see it any more. */
  get mediaUnavailable(): boolean {
    return this.mediaState === 'unavailable';
  }

  get isPdf(): boolean {
    return this.item?.content_type === 'application/pdf';
  }

  get isVideo(): boolean {
    return !!this.item?.content_type?.startsWith('video/');
  }

  get isAudio(): boolean {
    return !!this.item?.content_type?.startsWith('audio/');
  }

  get isImage(): boolean {
    return !!this.item?.content_type?.startsWith('image/');
  }

  /** Media of a document list: a tile in the row, played in a modal. */
  get playsInModal(): boolean {
    return (this.isVideo || this.isAudio) && !this.inlineMedia;
  }

  /** The player of the row itself, as a chat has it. */
  get playsInline(): boolean {
    return (this.isVideo || this.isAudio) && this.inlineMedia;
  }

  // ponytail: the browser decodes the frame from the file itself, a few
  // hundred kB a video; a poster rendered by the server once lists get long.
  get thumbnailUrl(): string {
    return `${this.item.url}#t=0.1`;
  }

  /** What a tap on the row does, for its label. */
  get openLabel(): string {
    if (this.isPdf || this.isImage) return 'COMMON.LABELS.PREVIEW';
    if (!this.playsInModal) return 'COMMON.LABELS.DOWNLOAD';
    return this.isVideo
      ? 'COMPONENTS.MEDIA.PLAY_VIDEO'
      : 'COMPONENTS.MEDIA.PLAY_AUDIO';
  }

  private get shareKind(): ShareKind {
    if (this.isVideo) return 'video';
    if (this.isAudio) return 'audio';
    return this.isImage ? 'image' : 'document';
  }

  get hasDocumentPage(): boolean {
    return this.documentId !== null;
  }

  private get documentId(): number | null {
    const id = Number(this.item?.id);
    return !this.articleId && Number.isInteger(id) && id > 0 ? id : null;
  }

  constructor(
    private modalService: ModalService,
    private documentService: DocumentService,
    private documentRepository: DocumentRepository,
    private articleRepository: ArticleRepository,
    private alertController: AlertController,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private appLinks: AppLinkService,
    private notificationService: NotificationService
  ) {}

  // A row that is handed another document starts over; the same document bound
  // again (a parent re-render, a refreshed thread) keeps what its player has
  // been through, or the renewal would start again on every pass.
  ngOnChanges(changes: SimpleChanges) {
    const item = changes['item'];
    if (item && item.previousValue?.id !== item.currentValue?.id) {
      this.urlRenewed = false;
      this.thumbFailed = false;
      this.setMediaState('playing');
    }
  }

  onThumbError() {
    this.thumbFailed = true;
    this.cdr.markForCheck();
  }

  open() {
    if (this.isPdf || this.isImage) return this.openDocument();
    if (this.playsInModal) return this.playMedia();
    this.downloadDocument();
  }

  // Signed right before it plays, like a PDF, so a list left open for hours
  // still plays. A video gets the screen, a recording a sheet.
  playMedia() {
    if (this.opening) return;
    this.opening = true;
    this.freshUrl$()
      .pipe(take(1))
      .subscribe(url => {
        this.opening = false;
        if (!url) return this.goToUnavailablePage();
        this.modalService.presentAndGetResult(
          this.isVideo ? VideoModalComponent : AudioModalComponent,
          { url, title: this.item.title }
        );
      });
  }

  // An expired signature and a codec the browser has no decoder for (an iPhone
  // .mov, say) both report MEDIA_ERR_SRC_NOT_SUPPORTED, so the code cannot tell
  // them apart. The first failure therefore renews the url once and mounts the
  // player again; only a failure on that fresh url means the file itself.
  onMediaError(event?: Event) {
    const code = (event?.target as HTMLMediaElement | null)?.error?.code;

    // The reader stopped the load, nothing refused anything.
    if (code === MEDIA_ERR_ABORTED) return;

    // The decoder gave up on bytes it did receive, or the renewed url failed
    // as well: either way it is the file, not the link.
    if (code === MEDIA_ERR_DECODE || this.urlRenewed) {
      return this.setMediaState('unplayable');
    }

    this.urlRenewed = true;
    this.reloadMedia();
  }

  // Nothing signs a poster, so until the first frame is decoded a thread of
  // videos is a wall of black rectangles. The placeholder names the file until
  // then.
  onMediaReady() {
    this.mediaReady = true;
    this.cdr.markForCheck();
  }

  // Re-reads the record, which hands out a freshly signed url, and lets the
  // player mount again with it. Whatever the answer, the card ends in a state
  // of its own: a player that fails never navigates anywhere by itself.
  private reloadMedia() {
    const id = Number(this.item.id);
    // Nothing to re-read, so nothing can renew the signature either.
    if (!Number.isInteger(id) || id <= 0) {
      return this.setMediaState('unplayable');
    }

    this.setMediaState('renewing');

    this.renewUrl$(id)
      .pipe(take(1))
      .subscribe(renewal => {
        if (renewal.kind === 'gone') return this.setMediaState('unavailable');

        // The re-read itself failed, which says nothing about the file, so the
        // player goes back up with the url it has. Its one renewal is spent,
        // so a second refusal ends as unplayable rather than in another fetch.
        if (renewal.kind === 'failed') return this.setMediaState('playing');

        // The record handed back the url that just failed - mounting it again
        // would only fail again.
        if (renewal.url === this.item.url) {
          return this.setMediaState('unplayable');
        }

        this.item = { ...this.item, url: renewal.url };
        this.setMediaState('playing');
      });
  }

  // Every end of the reload goes through here, so the spinner cannot outlive
  // it, and a player that mounts again always starts over: it names the file
  // until it has decoded a frame.
  private setMediaState(state: MediaState) {
    this.mediaState = state;
    this.mediaReady = false;
    this.cdr.markForCheck();
  }

  openDocument() {
    if (this.isPdf || this.isImage) {
      if (this.opening) return;
      this.opening = true;
      this.freshUrl$()
        .pipe(take(1))
        .subscribe(url => {
          this.opening = false;
          if (!url) return this.goToUnavailablePage();
          // An image opens like a chat picture: full screen, pinch to zoom.
          if (this.isImage) {
            this.modalService.presentAndGetResult(
              GallerySlideshowModalComponent,
              { images: [{ url }] }
            );
            return;
          }
          this.modalService.presentAndGetResult(PdfModalComponent, {
            pdfUrl: url,
            title: this.item.title
          });
        });
    } else {
      this.openExternally();
    }
  }

  downloadDocument() {
    this.openExternally();
  }

  private openExternally() {
    if (Capacitor.isNativePlatform()) {
      this.freshUrl$()
        .pipe(take(1))
        .subscribe(url =>
          url ? Browser.open({ url }) : this.goToUnavailablePage()
        );
      return;
    }

    // The tab must be claimed within the click's call stack — opening it
    // after the async fetch would trip popup blockers.
    const tab = window.open('', '_blank');
    this.freshUrl$()
      .pipe(take(1))
      .subscribe(url => {
        if (!url) {
          tab?.close();
          return this.goToUnavailablePage();
        }
        if (tab) {
          tab.location.href = url;
        } else {
          // Popup blocked — same-tab navigation is the only reliable fallback.
          window.location.assign(url);
        }
      });
  }

  shareLink() {
    this.appLinks.share(
      this.item.title,
      `/documents/${this.documentId}`,
      this.shareKind
    );
  }

  private goToUnavailablePage() {
    if (this.documentId === null) {
      this.notificationService.error(_('COMPONENTS.MEDIA.UNAVAILABLE'));
      return;
    }
    this.router.navigate(['/documents', this.documentId]);
  }

  // The stored url may be older than its signature allows; re-read the
  // document at open time. Transient failures fall back to the stored url;
  // a definitive 403/404 yields null so callers show the friendly state.
  private freshUrl$(): Observable<string | null> {
    const stored = this.item.url;
    const id = Number(this.item.id);
    if (!Number.isInteger(id) || id <= 0) return of(stored);

    return this.record$(id).pipe(
      map(document => document?.url || stored),
      catchError(err =>
        err?.status === 403 || err?.status === 404 ? of(null) : of(stored)
      )
    );
  }

  // What the reload needs on top of freshUrl$: why there is no fresh url. A
  // re-read that failed leaves the file alone, a document the reader may not
  // see does not.
  private renewUrl$(id: number): Observable<UrlRenewal> {
    return this.record$(id).pipe(
      map(
        (document): UrlRenewal =>
          document?.url
            ? { kind: 'renewed', url: document.url }
            : { kind: 'failed' }
      ),
      catchError(
        (err): Observable<UrlRenewal> =>
          of(
            err?.status === 403 || err?.status === 404
              ? { kind: 'gone' }
              : { kind: 'failed' }
          )
      )
    );
  }

  // The two endpoints an attachment can be re-read from.
  private record$(id: number): Observable<BasicDocument> {
    return this.articleId
      ? this.articleRepository.findDocument(this.articleId, id)
      : this.documentRepository.findById(id);
  }

  async promptRename() {
    const alert = await this.alertController.create({
      header: this.translate.instant('COMMON.LABELS.RENAME'),
      inputs: [
        {
          name: 'title',
          type: 'text',
          value: this.item.title,
          placeholder: this.item.title
        }
      ],
      buttons: [
        {
          text: this.translate.instant('COMMON.LABELS.CANCEL'),
          role: 'cancel'
        },
        {
          text: this.translate.instant('COMMON.LABELS.SAVE'),
          handler: data => {
            if (data.title?.trim() && data.title.trim() !== this.item.title) {
              this.documentRepository
                .update({
                  id: Number(this.item.id),
                  title: data.title.trim()
                })
                .subscribe(() => {
                  this.item = { ...this.item, title: data.title.trim() };
                  this.cdr.markForCheck();
                });
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async promptMove() {
    const item = this.item as FlDocument;
    const res = await this.modalService.presentAndGetResult<
      MoveToFolderModalProps,
      MoveToFolderModalResult
    >(MoveToFolderModalComponent, {
      name: item.title,
      originType: item.origin_type,
      originId: item.origin_id,
      currentId: item.category?.id ?? null,
      // A filed document lives in its folder's view.
      participation: item.category
        ? item.category.show_in_participation_page
        : item.show_in_participation_page
    });
    if (!res) return;

    this.documentRepository
      .update({ id: item.id, category_id: res.parentId })
      .subscribe({
        next: () => {
          this.notificationService.success(_('MODALS.MOVE_TO_FOLDER.MOVED'));
          this.changed.emit();
        },
        // A document of that title already there, or the folder was deleted.
        error: e =>
          this.notificationService.error(
            e?.error?.title
              ? _('MODALS.MOVE_TO_FOLDER.NAME_TAKEN')
              : _('MODALS.MOVE_TO_FOLDER.FAILED')
          )
      });
  }

  async confirmAndDelete() {
    const confirmed = await this.modalService.presentAndGetResult(
      ConfirmModalComponent,
      {
        cancelLabel: _('COMMON.LABELS.CANCEL'),
        confirmLabel: _('COMMON.LABELS.DELETE'),
        confirmColor: 'danger',
        title: _('MODALS.OPTIONS.DOCUMENT.DELETE_TITLE'),
        description: _('MODALS.OPTIONS.DOCUMENT.DELETE_DESCRIPTION')
      }
    );

    if (confirmed) {
      this.documentService
        .delete(Number(this.item.id))
        .subscribe(() => this.changed.emit());
    }
  }
}
